/**
 * dateUtils.ts — Consistent local-date utilities.
 *
 * Root cause of the "off-by-one day" bug:
 *   `new Date('2026-02-08')` parses YYYY-MM-DD as **UTC midnight**.
 *   In any timezone west of UTC the resulting local date is the *previous* day.
 *
 * These helpers ensure dates are always interpreted in local time.
 */

// ── Parsing ──────────────────────────────────────────────────────────

/**
 * Parse a date-only string (YYYY-MM-DD) as **local midnight**, not UTC.
 *
 * Also handles the extremely common backend pattern where a date-only value
 * is stored as a full ISO timestamp at UTC midnight, e.g.:
 *   "2026-02-08T00:00:00.000Z"  or  "2026-02-08T00:00:00Z"
 * These are treated as **local** Feb 8, not UTC Feb 8 (which would display
 * as Feb 7 in US timezones).
 *
 * For datetime strings with a meaningful (non-midnight-UTC) time component,
 * falls back to normal `new Date()` parsing.
 *
 * Returns `null` for unparseable / empty values.
 */
export function parseLocalDate(value: unknown): Date | null {
  if (value === null || value === undefined) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value

  const str = String(value).trim()
  if (!str) return null

  // Extract the leading YYYY-MM-DD from any string that starts with one.
  const leadingDate = str.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (leadingDate) {
    const y = Number(leadingDate[1])
    const m = Number(leadingDate[2])
    const d = Number(leadingDate[3])

    // Case 1: bare "YYYY-MM-DD" → always local
    // Case 2: "YYYY-MM-DDT00:00:00Z", "YYYY-MM-DDT00:00:00.000Z",
    //         "YYYY-MM-DDT00:00:00.000+00:00", etc.
    //         These are date-only values that a backend wrapped in UTC midnight.
    //         Treat them as local dates too.
    const rest = str.slice(10) // everything after YYYY-MM-DD
    const isDateOnly = rest === ''
    const isMidnightUTC = /^T00:00:00(\.0+)?(Z|[+-]00:?00)?$/.test(rest)

    if (isDateOnly || isMidnightUTC) {
      const date = new Date(y, m - 1, d)
      if (date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d) {
        return date
      }
      return null
    }
  }

  // Everything else (ISO datetime with real time, timestamps, etc.)
  const parsed = new Date(str)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

// ── Formatting to YYYY-MM-DD key ────────────────────────────────────

/**
 * Format a Date to YYYY-MM-DD using **local** year/month/day.
 * This replaces the error-prone `date.toISOString().split('T')[0]` pattern
 * which returns the UTC date (can be ±1 day from local).
 */
export function toLocalDateKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Return today's date as a YYYY-MM-DD string in local time.
 */
export function todayKey(): string {
  return toLocalDateKey(new Date())
}

/**
 * True when `value` parses to a calendar day strictly before today (local time).
 * Unknown / unparseable dates return false (not treated as past).
 */
export function isPastLocalDate(value: unknown): boolean {
  const d = parseLocalDate(value)
  if (!d) return false
  return toLocalDateKey(d) < todayKey()
}

/**
 * Return a YYYY-MM-DD key for a Date that is `offsetDays` from today.
 * Positive = future, negative = past.
 */
export function offsetDayKey(offsetDays: number): string {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  return toLocalDateKey(d)
}

// ── Display helpers ─────────────────────────────────────────────────

/**
 * Safely parse a date string and display it with `toLocaleDateString`.
 * Returns `fallback` when the value is empty or unparseable.
 */
export function localDateDisplay(
  value: unknown,
  options?: Intl.DateTimeFormatOptions,
  fallback = '—',
): string {
  const d = parseLocalDate(value)
  if (!d) return fallback
  return d.toLocaleDateString('en-US', options)
}

/**
 * Short display: "Feb 8" style.
 */
export function shortDateDisplay(value: unknown, fallback = '—'): string {
  return localDateDisplay(value, { month: 'short', day: 'numeric' }, fallback)
}
